import {
  brandOptions,
  organizations,
  suppliers,
  contactPersons,
  adminUnits,
  categories,
} from './itemsFixtureData';

/**
 * Deterministic probes for the bulk-edit clear-field GATING & ELIGIBILITY epic (dl-62704, Items
 * slice: dl-62708/dl-62711/dl-62713). Isolable via `?search=ClearFieldGatingProbe` (unique
 * substring, absent from every generated/other fixture name), with a sub-phrase per scenario for
 * further narrowing (e.g. `?search=ClearFieldGatingProbe Flag`).
 *
 * Split out of `items.ts` — same reason `bulkUpdateProbeFixtureData.ts` was: keeps the handler
 * file under the repo's 800-line cap (`.eslintrc.js` `max-lines`). Base shape mirrors
 * `bulkUpdateProbeBase`; every field a test cares about is set explicitly on that scenario's
 * items rather than inherited, so a shared-base edit later can't silently change what a test
 * asserts.
 *
 * `brand` / `companyContactPerson` (Brand contact) / `isActive` (Status) / `defaultQty` (Default
 * quantity) are the four fields `itemValidationRules` (`features/items/utils/validation.ts`)
 * marks `required: true` — every probe below carries a real, non-null value for all four so the
 * "required fields never show Set-empty" assertions (dl-62711) have an existing value to prove
 * the absence isn't merely "nothing to clear yet" (dl-62713's own, separate concern).
 */
const clearFieldGatingProbeBase = {
  organization: organizations[0],
  supplier: suppliers[0],
  companyContactPerson: contactPersons[0],
  supplierContactPerson: null,
  logo: null,
  suppliedBy: null,
  readyForExport: false,
  updatedAfterLastExport: false,
  lastUpdated: '2026-01-15T09:00:00',
  deadline: null,
  etd: null,
  erpItemId: null,
  nameGeneratorExtraText: null,
  physicalReferenceNumber: null,
  description: null,
  brand: brandOptions[0],
  isActive: true,
  defaultQty: 5,
  customFields: [],
};

/**
 * dl-62708 (Testmo 2339504) — Flag-gating pair: BOTH items share the same existing Unit
 * (`adminUnit`) and Categories value (non-mixed, so `currentDisplay` is the real shared value,
 * not "Mixed values") — i.e. eligible optional fields that WOULD offer "Set empty" once
 * `BulkEditClearField` is ON, so the OFF-direction assertion ("no Set-empty anywhere") is
 * actually exercising the flag gate and not just "nothing to clear".
 */
export const clearFieldGatingFlagA = {
  ...clearFieldGatingProbeBase,
  id: 90310,
  name: 'ClearFieldGatingProbe Flag A',
  userDefinedId: 'IT-90310',
  adminUnit: adminUnits[0],
  categories: [categories[0]],
};
export const clearFieldGatingFlagB = {
  ...clearFieldGatingProbeBase,
  id: 90311,
  name: 'ClearFieldGatingProbe Flag B',
  userDefinedId: 'IT-90311',
  adminUnit: adminUnits[0],
  categories: [categories[0]],
};

/**
 * dl-62711 (Testmo 2339506) — Required-fields pair. Reuses the same shared-value shape as the
 * Flag pair (existing Unit/Categories value too) so the same 2 items double as "an eligible
 * OPTIONAL field DOES offer Set-empty" contrast evidence right next to the required fields that
 * must never offer it, all within one modal open.
 */
export const clearFieldGatingRequiredA = {
  ...clearFieldGatingProbeBase,
  id: 90312,
  name: 'ClearFieldGatingProbe Required A',
  userDefinedId: 'IT-90312',
  adminUnit: adminUnits[0],
  categories: [categories[0]],
};
export const clearFieldGatingRequiredB = {
  ...clearFieldGatingProbeBase,
  id: 90313,
  name: 'ClearFieldGatingProbe Required B',
  userDefinedId: 'IT-90313',
  adminUnit: adminUnits[0],
  categories: [categories[0]],
};

/**
 * dl-62713 (Testmo 2339511) — "no existing value" pair: BOTH items empty on the optional Unit AND
 * Categories fields (holds across the dropdown AND multiselect field types).
 */
export const clearFieldGatingEmptyA = {
  ...clearFieldGatingProbeBase,
  id: 90314,
  name: 'ClearFieldGatingProbe Empty A',
  userDefinedId: 'IT-90314',
  adminUnit: null,
  categories: [],
};
export const clearFieldGatingEmptyB = {
  ...clearFieldGatingProbeBase,
  id: 90315,
  name: 'ClearFieldGatingProbe Empty B',
  userDefinedId: 'IT-90315',
  adminUnit: null,
  categories: [],
};

/**
 * dl-62713 contrast — paired with `clearFieldGatingEmptyA` in a 2-item selection: ONE item has a
 * real Unit/Categories value, the other has none, so the selection is "mixed presence" (not
 * "all empty") and `hasExistingValue` (`!!currentDisplay` in `BulkEditModal.tsx`) resolves true
 * via the "Mixed values" preview — Set-empty must appear even though it isn't a single shared
 * value.
 */
export const clearFieldGatingMixedHasValue = {
  ...clearFieldGatingProbeBase,
  id: 90316,
  name: 'ClearFieldGatingProbe MixedHasValue',
  userDefinedId: 'IT-90316',
  adminUnit: adminUnits[1],
  categories: [categories[1]],
};
